/**
 * Toggle hook utilities
 * Provides a single toggle state management with global coordination
 */

import { useCallback, useEffect, useRef, useState } from 'react';

type ToggleRef = {
  current: () => void;
};

/**
 * useSingleToggle Hook
 * 
 * Manages a boolean toggle state with global coordination to ensure
 * only one tooltip/popover is open at a time.
 * 
 * @param globalRef - Reference to global toggle function for coordinating multiple instances
 * @returns [showing, toggle, show, hide] - State and control functions
 * 
 * @example
 * ```tsx
 * const globalRef = { current: () => {} };
 * const [showing, toggle, show, hide] = useSingleToggle(globalRef);
 * ```
 */
export function useSingleToggle(
  globalRef: ToggleRef
): [boolean, () => void, () => void, () => void] {
  const [showing, setShowing] = useState(false);
  const isMountedRef = useRef(true);

  // Hide this instance
  const hide = useCallback(() => {
    if (isMountedRef.current) {
      setShowing(false);
    }
  }, []);

  // Show this instance (and hide others)
  const show = useCallback(() => {
    if (isMountedRef.current) {
      // Hide any other open tooltip
      globalRef.current();
      // Update global ref to point to this instance's hide function
      globalRef.current = hide;
      setShowing(true);
    }
  }, [globalRef, hide]);

  // Toggle this instance
  const toggle = useCallback(() => {
    if (showing) {
      hide();
    } else {
      show();
    }
  }, [showing, hide, show]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      // Clear global ref if it points to this instance
      if (globalRef.current === hide) {
        globalRef.current = () => {};
      }
    };
  }, [globalRef, hide]);

  return [showing, toggle, show, hide];
}

